import React, { useCallback, useState, useRef, useEffect } from 'react';
import { IconButton, MinusIcon, PlusIcon } from 'fronton-react';
import { cx } from 'linaria';
import { useTranslation } from 'react-i18next';
import {
  counterBody,
  counterBodyError,
  decrementButton,
  incrementButton,
  button,
  inputField,
} from './qty-counter.styles';

type Props = {
  min?: number;
  max?: number;
  onChange?: (c: number) => void;
  onRemove?: () => void;
  step?: number;
  overriddenValue?: number;
  invalid?: boolean;
  className?: string;
  uom?: string;
  isStickyPanel?: boolean;
};

const IS_INVALID_INPUT = /[^0-9.,]/g;

export const QtyCounter: React.FC<Props> = ({
  min = 1,
  max = 999,
  step = 1,
  onChange,
  onRemove,
  overriddenValue,
  invalid = false,
  className,
  isStickyPanel,
  uom = '',
}: Props) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const inputMode = step < 1 ? 'decimal' : 'numeric';

  const [count, setCount] = useState<number>(overriddenValue || min);
  const [inputValue, setInputValue] = useState<string | number>(overriddenValue || min);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const applyValue = useCallback(
    (value: number) => {
      const normalizedValue = parseFloat(value.toFixed(2));
      setInputValue(normalizedValue);
      setCount(normalizedValue);
      if (count !== value) {
        if (normalizedValue < min) {
          onRemove?.();
        } else {
          onChange?.(normalizedValue);
        }
      }
    },
    [count, min, onChange, onRemove],
  );

  const decrement = useCallback(() => {
    applyValue(count - step);
  }, [applyValue, count, step]);

  const increment = useCallback(() => {
    applyValue(count + step <= max ? count + step : max);
  }, [applyValue, count, max, step]);
  const handleInput = () => {
    const normalizedValue = String(inputValue).replace(',', '.');
    const countNumber = step * Math.ceil(parseFloat(normalizedValue) / step);
    const value = countNumber > max ? max : countNumber || 0;
    applyValue(value);

    setIsFocused(false);
  };
  const handleKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    let { key, target } = e;
    if (key === 'Enter' || key === 'Escape') {
      (target as HTMLInputElement).blur();
    }
    if (key === 'ArrowUp') {
      increment();
    }
    if (key === 'ArrowDown') {
      decrement();
    }
    e.preventDefault();

    if (key === 'ArrowLeft' && typeof inputRef?.current?.selectionEnd === 'number') {
      inputRef.current.selectionEnd = inputRef?.current?.selectionEnd - 1;
      return;
    }
    if (key === 'ArrowRight' && typeof inputRef?.current?.selectionStart === 'number') {
      inputRef.current.selectionStart = inputRef?.current?.selectionStart + 1;
      return;
    }

    let input = inputValue;
    let { selectionStart, selectionEnd } = inputRef?.current || {};
    const selected = (selectionEnd as number) - (selectionStart as number);

    if (key.match(IS_INVALID_INPUT) && key !== 'Backspace') {
      return;
    }

    if (key === 'Backspace') {
      if (!selected && selectionStart) {
        --selectionStart;
      }
      key = '';
    }

    input = `${input}`.slice(0, selectionStart as number) + key + `${input}`.slice(selectionEnd as number);
    setInputValue(input);
  };
  const handleFocus = () => {
    setIsFocused(true);
    setTimeout(() => inputRef.current?.select(), 10);
  };

  useEffect(() => {
    if (overriddenValue !== undefined) {
      applyValue(overriddenValue as number);
    }
  }, [overriddenValue]);

  return (
    <div data-qa="input-container" className={cx(counterBody, className, invalid ? counterBodyError : null)}>
      <IconButton
        data-qa="button-minus"
        className={cx(button, decrementButton)}
        onClick={decrement}
        variant="primary"
        label={t('ui.buttons.less')}
        size="m"
      >
        <MinusIcon />
      </IconButton>
      <input
        data-qa="add-to-basket-input"
        className={inputField}
        onFocus={handleFocus}
        onBlur={handleInput}
        onKeyDown={handleKeys}
        min={min}
        max={max}
        step={step}
        inputMode={inputMode}
        value={isFocused ? inputValue : `${inputValue} ${uom}`.trim()}
        ref={inputRef}
        disabled={isStickyPanel}
      />
      <IconButton
        data-qa="button-plus"
        className={cx(button, incrementButton)}
        onClick={increment}
        variant="primary"
        label={t('ui.buttons.more')}
        size="m"
      >
        <PlusIcon />
      </IconButton>
    </div>
  );
};
